import { faPhone, faPhoneVolume } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	Dialog,
	DialogPanel,
	DialogTitle,
	Transition,
	TransitionChild,
} from '@headlessui/react';
import React, { Fragment, useState } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { Button } from '../forms/Button';
import { Checkbox } from '../forms/Checkbox';

export type CallMethodType = 'vonage' | 'ringcentral' | 'phone_app';

export interface CallMethodModalProps {
	isOpen: boolean;
	onClose: () => void;
	phoneNumber: string;
	availableIntegrations: Array<'vonage' | 'ringcentral'>;
	onSelectMethod: (method: CallMethodType, remember: boolean) => void;
}

export function CallMethodModal({
	isOpen,
	onClose,
	phoneNumber,
	availableIntegrations,
	onSelectMethod,
}: CallMethodModalProps): React.ReactElement {
	const colors = useThemeColors();
	const { t } = useTranslation();
	const [rememberChoice, setRememberChoice] = useState(false);

	const handleSelectIntegration = (
		integration: 'vonage' | 'ringcentral',
	): void => {
		onSelectMethod(integration, rememberChoice);
		onClose();
	};

	const handlePhoneApp = (): void => {
		onSelectMethod('phone_app', rememberChoice);
		onClose();
	};

	const hasVonage = availableIntegrations.includes('vonage');
	const hasRingCentral = availableIntegrations.includes('ringcentral');

	return (
		<Transition appear show={isOpen} as={Fragment}>
			<Dialog as="div" className="relative z-50" onClose={onClose}>
				<TransitionChild
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-black bg-opacity-25" />
				</TransitionChild>

				<div className="fixed inset-0 overflow-y-auto">
					<div className="flex min-h-full items-center justify-center p-4 text-center">
						<TransitionChild
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 scale-95"
							enterTo="opacity-100 scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 scale-100"
							leaveTo="opacity-0 scale-95"
						>
							<DialogPanel
								className={`w-full max-w-md transform rounded-lg ${colors.classes.bgPrimary} text-left align-middle shadow-xl transition-all`}
							>
								<div
									className={`px-6 py-4 border-b ${colors.classes.borderDefault}`}
								>
									<DialogTitle
										as="h3"
										className={`text-lg font-medium leading-6 ${colors.classes.textPrimary}`}
									>
										{t('How would you like to call?')}
									</DialogTitle>
								</div>

								<div className="px-6 py-4 space-y-4">
									<p className={`text-sm ${colors.classes.textSecondary}`}>
										{t('Choose how to call')} {phoneNumber}
									</p>

									<div className="space-y-3">
										{hasVonage ? (
											<button
												type="button"
												onClick={() => {
													handleSelectIntegration('vonage');
												}}
												className={`w-full flex items-center gap-3 p-4 rounded-lg border ${colors.classes.borderDefault} hover:${colors.classes.bgHover} transition-colors text-left`}
											>
												<div
													className={`w-10 h-10 rounded-full flex items-center justify-center ${colors.classes.bgSuccess}`}
												>
													<FontAwesomeIcon
														icon={faPhoneVolume}
														className="text-white"
													/>
												</div>
												<div>
													<div
														className={`font-medium ${colors.classes.textPrimary}`}
													>
														{t('Use Vonage')}
													</div>
													<div
														className={`text-sm ${colors.classes.textSecondary}`}
													>
														{t('Call using your Vonage extension')}
													</div>
												</div>
											</button>
										) : null}

										{hasRingCentral ? (
											<button
												type="button"
												onClick={() => {
													handleSelectIntegration('ringcentral');
												}}
												className={`w-full flex items-center gap-3 p-4 rounded-lg border ${colors.classes.borderDefault} hover:${colors.classes.bgHover} transition-colors text-left`}
											>
												<div
													className={`w-10 h-10 rounded-full flex items-center justify-center ${colors.classes.bgSuccess}`}
												>
													<FontAwesomeIcon
														icon={faPhoneVolume}
														className="text-white"
													/>
												</div>
												<div>
													<div
														className={`font-medium ${colors.classes.textPrimary}`}
													>
														{t('Use RingCentral')}
													</div>
													<div
														className={`text-sm ${colors.classes.textSecondary}`}
													>
														{t('Call using your RingCentral account')}
													</div>
												</div>
											</button>
										) : null}

										<button
											type="button"
											onClick={handlePhoneApp}
											className={`w-full flex items-center gap-3 p-4 rounded-lg border ${colors.classes.borderDefault} hover:${colors.classes.bgHover} transition-colors text-left`}
										>
											<div
												className={`w-10 h-10 rounded-full flex items-center justify-center ${colors.classes.bgInfo}`}
											>
												<FontAwesomeIcon
													icon={faPhone}
													className="text-white"
												/>
											</div>
											<div>
												<div
													className={`font-medium ${colors.classes.textPrimary}`}
												>
													{t('Use phone app')}
												</div>
												<div
													className={`text-sm ${colors.classes.textSecondary}`}
												>
													{t('Open in your default phone application')}
												</div>
											</div>
										</button>
									</div>

									<div className="flex items-center gap-2 pt-2">
										<Checkbox
											checked={rememberChoice}
											onChange={setRememberChoice}
											label={t('Remember my choice')}
										/>
									</div>

									<p
										className={`text-xs ${colors.classes.textMuted} italic pt-1`}
									>
										{t(
											'You can reset this choice in your Profile settings under Call Preferences.',
										)}
									</p>
								</div>

								<div
									className={`flex justify-end px-6 py-4 border-t ${colors.classes.borderDefault}`}
								>
									<Button variant="outline" onClick={onClose}>
										{t('Cancel')}
									</Button>
								</div>
							</DialogPanel>
						</TransitionChild>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
}
